<template>
  <Transition name="slide-fade" appear>
    <div v-if="visible" class="modal">
      <div class="modal-content">
        <div class="page-header">
          <h2 class="page-title">{{ taskType === 'customProgram' ? $t('add_program_task') : taskType === 'customVisualTest' ? $t('add_visual_test_task') : $t('add_test_task') }}</h2>
        </div>

        <div class="dot"></div>

        <div class="search-section">
          <span class="form-title">{{ taskType === 'customProgram' ? $t('program_name') : taskType === 'customVisualTest' ? $t('visual_test_name') : $t('test_name') }}</span>
          <div class="search-container">
            <div class="select-style" @click="toggleDropdown">
              <input
                  type="text"
                  class="search-input-inline"
                  :placeholder="taskType === 'customProgram' ? $t('search_program') : taskType === 'customVisualTest' ? $t('search_visual_test') : $t('search_test')"
                  v-model="searchQuery"
                  @input="searchItems"
                  @focus="handleFocus"
                  @blur="handleBlur"
                  @click.stop
              />
              <div class="arrow" :class="{'open': showDropdown}">
                <img src="/src/assets/images/arrowdown.svg" alt="arrow" />
              </div>
            </div>

            <Transition name="dropdown">
              <div v-if="showDropdown" class="dropdown" ref="dropdownMenu">
                <div v-if="isSearchLoading && currentPage === 1" class="dropdown-loading">
                  <div class="spinner"></div>
                  <span>{{ $t("loading") }}</span>
                </div>

                <div v-else-if="searchQuery && displayItems.length === 0 && !isSearchLoading" class="dropdown-empty">
                  <p>{{ $t("no_data_found") }}</p>
                </div>

                <div v-else class="dropdown-results" @scroll="handleScroll">
                  <ul class="options-list">
                    <li
                        v-for="item in displayItems"
                        :key="item.programId || item.testId || item.gameLabsId"
                        class="option-item"
                        @mousedown="selectItem(item)"
                        :class="{'selected': isItemSelected(item)}"
                    >
                      <div class="item-content">
                        <div class="item-name">
                          <span v-html="highlightMatch(item.name)"></span>
                        </div>
                      </div>
                    </li>
                  </ul>

                  <!-- Loading more indicator -->
                  <div v-if="isLoadingMore" class="loading-more">
                    <div class="spinner-small"></div>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </div>

        <div v-if="selectedItem" class="selected-item-container">
          <div class="selected-item">
            <div class="item-image">
              <img :src="selectedItem.avatarUrl" alt="Selected item" />
            </div>
            <div class="item-details">
              <h3>{{ selectedItem.name }}</h3>
              <p>{{ selectedItem.description || selectedItem.shortDescription || $t('no_description') }}</p>
            </div>
          </div>
        </div>

        <div class="xp-section">
          <span class="form-title">{{ $t('task_points') }}</span>
          <div class="input-with-icon">
            <img src="@/assets/images/xp-coin.svg" alt="Point" class="point-icon" />
            <input
                type="number"
                class="form-input with-icon"
                :placeholder="$t('enter_task_points')"
                v-model="xpPoints"
                min="1"
            />
          </div>
        </div>

        <div class="task-details">
          <div class="form-div">
            <span class="form-title">{{ $t('task_name') }}</span>
            <input class="form-input" :placeholder="$t('enter_task_name')" v-model="taskName" />
          </div>

          <div class="form-div">
            <span class="form-title">{{ $t('task_description') }}</span>
            <textarea
                class="form-input"
                :placeholder="$t('enter_task_description')"
                v-model="taskDescription"
                rows="3"
            ></textarea>
          </div>
        </div>

        <div class="action-buttons">
          <div></div>
          <div class="right-buttons">
            <button class="dark-button-outline" @click="onHide">{{ $t('cancel') }}</button>
            <button
                class="dark-button"
                @click="saveTask"
                :disabled="!canSave"
                :class="{'disabled': !canSave}"
            >
              <span v-if="!isLoading">{{ $t('save') }}</span>
              <VueSpinnerIos v-if="isLoading" size="20" height="100%" color="#fff" />
            </button>
          </div>
        </div>

        <div @click="onHide" class="close-btn">
          <img class="cross" src="@/assets/images/cross.svg" />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import { VueSpinnerIos } from 'vue3-spinners';
import gamificationService from '@/service/gamificationService';
import programService from '@/service/programService';
import testService from '@/service/testService';
import gameLabService from '@/service/gameLabService';
import { handleErrorResponse } from '@/utils/utils';

export default {
  name: 'AddTaskModal',
  components: {
    VueSpinnerIos
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    taskType: {
      type: String,
      default: 'customProgram',
      validator: (value) => ['customProgram', 'customTest', 'customVisualTest'].includes(value)
    },
    onHide: {
      type: Function,
      default: () => {
      }
    },
    onSuccess: {
      type: Function,
      default: () => {
      }
    },
    taskId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 1,
      allItems: [],
      displayItems: [],
      isLoading: false,
      isSearchLoading: false,
      isLoadingMore: false,
      searchQuery: '',
      selectedItem: null,
      xpPoints: 100,
      taskName: '',
      taskDescription: '',
      searchTimeout: null,
      showDropdown: false,
      hasMoreItems: true
    };
  },
  computed: {
    canSave() {
      return this.selectedItem && this.xpPoints > 0 && this.taskName && this.taskDescription;
    }
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        this.resetForm();
      }
    },
    selectedItem(newValue) {
      if (newValue) {
        if (this.taskType === 'customProgram') {
          this.taskName = `${this.$t('complete_program')}: ${newValue.name}`;
        } else if (this.taskType === 'customVisualTest') {
          this.taskName = `${this.$t('complete_visual_test')}: ${newValue.name}`;
        } else {
          this.taskName = `${this.$t('complete_test')}: ${newValue.name}`;
        }

        if (this.taskType === 'customProgram') {
          this.taskDescription = this.$t('complete_program_earn_xp');
        } else if (this.taskType === 'customVisualTest') {
          this.taskDescription = this.$t('complete_visual_test_earn_xp');
        } else {
          this.taskDescription = this.$t('complete_test_earn_xp');
        }
      }
    }
  },
  methods: {
    async loadItems(page = 1, isLoadMore = false) {
      if (this.isSearchLoading || this.isLoadingMore) return;

      try {
        if (isLoadMore) {
          this.isLoadingMore = true;
        } else {
          this.isSearchLoading = true;
        }

        let response;
        const filters = this.searchQuery ? [
          {
            key: 'searchText',
            value: this.searchQuery
          }
        ] : [];

        if (this.taskType === 'customProgram') {
          response = await programService.getPrograms(page, filters);
        } else if (this.taskType === 'customVisualTest') {
          response = await gameLabService.getGameLabs(page, filters);
        } else {
          response = await testService.getTests(page, filters);
        }

        if (response.data.responseType === 'success') {
          const newItems = response.data.data.data || [];

          if (isLoadMore) {
            this.displayItems = [...this.displayItems, ...newItems];
          } else {
            this.displayItems = newItems;
          }

          const totalItems = response.data.data.total || 0;
          const itemsPerPage = response.data.data.per_page || 20;
          this.totalPages = Math.ceil(totalItems / itemsPerPage);
          this.hasMoreItems = page < this.totalPages;

        } else {
          if (!isLoadMore) {
            this.displayItems = [];
          }
          this.hasMoreItems = false;
        }
      } catch (error) {
        console.error('Error loading items:', error);
        if (!isLoadMore) {
          this.displayItems = [];
        }
        this.hasMoreItems = false;
      } finally {
        this.isSearchLoading = false;
        this.isLoadingMore = false;
      }
    },

    resetForm() {
      this.searchQuery = '';
      this.displayItems = [];
      this.selectedItem = null;
      this.xpPoints = 100;
      this.taskName = '';
      this.taskDescription = '';
      this.showDropdown = false;
      this.currentPage = 1;
      this.hasMoreItems = true;
    },

    handleFocus() {
      this.showDropdown = true;
      if (this.displayItems.length === 0) {
        this.loadItems(1);
      }
    },

    handleBlur() {
      setTimeout(() => {
        this.showDropdown = false;
      }, 200);
    },

    toggleDropdown(event) {
      event.stopPropagation();
      this.showDropdown = !this.showDropdown;
      if (this.showDropdown && this.displayItems.length === 0) {
        this.loadItems(1);
      }
    },

    searchItems() {
      clearTimeout(this.searchTimeout);

      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadItems(1);
      }, 300);
    },

    handleScroll(event) {
      const element = event.target;
      const scrollPosition = element.scrollTop + element.clientHeight;
      const scrollHeight = element.scrollHeight;

      if (scrollPosition >= scrollHeight - 50 && this.hasMoreItems && !this.isLoadingMore) {
        this.currentPage++;
        this.loadItems(this.currentPage, true);
      }
    },

    selectItem(item) {
      this.selectedItem = item;
      this.searchQuery = item.name;
      this.showDropdown = false;
    },

    isItemSelected(item) {
      if (!this.selectedItem) return false;
      return (this.selectedItem.programId === item.programId) ||
          (this.selectedItem.testId === item.testId) ||
          (this.selectedItem.gameLabsId === item.gameLabsId);
    },

    highlightMatch(text) {
      if (!this.searchQuery) return text;

      const regex = new RegExp(`(${this.searchQuery})`, 'gi');
      return text.replace(regex, '<strong>$1</strong>');
    },

    async saveTask() {
      if (!this.canSave) return;

      try {
        this.isLoading = true;

        const taskData = {
          taskId: this.taskId,
          originalTaskId: this.taskId,
          name: this.taskName,
          description: this.taskDescription,
          xpPoints: parseInt(this.xpPoints),
          taskIdentifier: this.taskType === 'customProgram'
              ? this.selectedItem.programId
              : this.taskType === 'customVisualTest'
                  ? this.selectedItem.gameLabsId
                  : this.selectedItem.testId,
          isActive: true,
          taskType: this.taskType
        };

        const response = await gamificationService.customizeGamificationTask(taskData);

        if (response.data.responseType === 'success') {
          this.$snackbar.add({
            text: this.$t('task_added_successfully'),
            type: 'success'
          });
          this.onSuccess(response.data.data);
          this.onHide();

        } else {
          this.$snackbar.add({
            text: response.data.message || this.$t('task_add_error'),
            type: 'error'
          });
        }
      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001 !important;
}

.modal-content {
  background-color: white;
  border-radius: 12px;
  width: 650px;
  max-width: 95%;
  position: relative;
  padding: 32px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  margin-bottom: 24px;
}

.modal-header h1 {
  font-size: 24px;
  font-weight: 600;
  color: #101828;
}

.dot{
  display: flex;
  width: 100%;
  border: 1px solid #F5F5F5;
  margin-bottom: 20px;
}

.search-section, .xp-section, .task-details {
  margin-bottom: 24px;
}

.form-title {
  font-size: 14px;
  font-weight: 500;
  color: #344054;
  display: block;
  margin-bottom: 8px;
}

.search-container {
  position: relative;
  width: 100%;
}

.select-style {
  border-radius: 8px;
  border: 1px solid #F5F5F5;
  background: #F5F5F5;
  color: #282A36;
  font-size: 14px;
  padding: 15px 16px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.search-input-inline {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  color: #282A36;
  padding: 0;
}

.search-input-inline::placeholder {
  color: #666;
}

.arrow {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  flex-shrink: 0;
  margin-left: 8px;
}

.arrow img {
  width: 16px;
  height: 16px;
}

.arrow.open {
  transform: rotate(180deg);
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff !important;
  border: 1px solid #CDCDCD !important;
  border-radius: 8px;
  max-height: 400px;
  overflow: hidden;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 4px;
}

.dropdown-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #667085;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #F2F4F7;
  border-top-color: #0052CC;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 12px;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid #F2F4F7;
  border-top-color: #0052CC;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.dropdown-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}

.dropdown-empty p {
  font-size: 14px;
  color: #667085;
  margin: 0;
}

.dropdown-results {
  overflow-y: auto;
  max-height: 400px;
}

.dropdown-results::-webkit-scrollbar {
  width: 15px;
}

.dropdown-results::-webkit-scrollbar-track {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  background-color: #F5F5F5;
}

.dropdown-results::-webkit-scrollbar-thumb {
  background-color: rgba(4, 12, 45, 0.2);
  border-radius: 10px;
  border: 5px transparent solid;
  background-clip: padding-box;
}

.dropdown-results::-webkit-scrollbar-thumb:hover {
  background-color: rgba(4, 12, 45, 0.4);
}

.options-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.option-item {
  padding: 12px;
  cursor: pointer;
  color: #282A36 !important;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s;
}

.option-item:hover {
  background-color: #f8f9fa;
}

.option-item.selected {
  background-color: #F0F7FF;
}

.option-item:last-child {
  border-bottom: none;
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 14px;
  font-weight: 500;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-name strong {
  background-color: #FEF3C7;
  font-weight: 600;
}

.loading-more {
  display: flex;
  justify-content: center;
  padding: 12px;
}

/* Dropdown transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  transform: translateY(-8px);
  opacity: 0;
}

/* Selected Item Container */
.selected-item-container {
  margin-top: 16px;
  margin-bottom: 24px;
}

.selected-item {
  display: flex;
  align-items: center;
  padding: 14px;
  border-radius: 8px;
  background-color: #F9FAFB;
  border: 1px solid #EAECF0;
}

.item-image {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  margin-right: 14px;
  background-color: #F2F4F7;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.item-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.item-details h3 {
  font-size: 16px;
  font-weight: 500;
  color: #101828;
  margin: 0 0 4px 0;
}

.item-details p {
  font-size: 14px;
  color: #475467;
  margin: 0;
}

.input-with-icon {
  position: relative;
  width: 100%;
}

.form-input.with-icon {
  padding-left: 40px;
}

.point-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  pointer-events: none;
}

.form-div {
  margin-bottom: 16px;
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
}

.right-buttons {
  display: flex;
  gap: 12px;
}

.dark-button.disabled {
  cursor: not-allowed;
  color: #060B27;
}

.close-btn {
  position: absolute;
  top: 8px;
  right: 6px;
  cursor: pointer;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.2s;
  background-color: #f2f4f7;
  margin: 10px;
}

.cross {
  width: 16px;
  height: 16px;
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
